import { getCursorPositionStyles } from '@/utils/tinyMCE/getCursorPositionStyles'
import { pinEditorBlockFonts } from '@/utils/tinyMCE/pinEditorBlockFonts'
import { regEx } from '@/utils/global/regEx'

/**
 * Normalizes whitespace in HTML content while preserving tags
 * Collapses multiple spaces between text nodes into single spaces
 */
const normalizeHTMLWhitespace = (html: string): string => {
  // Replace multiple spaces/tabs/newlines between > and < with a single space
  return html
    .replace(regEx.whitespaceBetweenTags, '> <')
    .replace(regEx.multipleWhiteSpace, ' ')
}

/**
 * Sets up paste event handler to preserve formatting from cursor position
 * When pasting text, applies the current line's inline formatting to pasted content
 * Preserves existing HTML structure including line breaks (<br> tags)
 * Uses getCursorPositionStyles for inline styles (font, color, etc.)
 *
 * Note: Does NOT apply block-level styles (line-height, text-align) to allow
 * TinyMCE to manage layout dynamically when text box is resized
 * Note: Does NOT apply bold/italic - these are handled via <strong>/<em> tags only
 */
export const pasteHandler = ({
  editor,
  plainTextMode,
}: {
  editor: Editor
  plainTextMode: boolean
}) => {
  if (plainTextMode) return
  // Use PastePostProcess instead of PastePreProcess because paste_as_text: true
  // strips formatting during PreProcess. PostProcess runs after content is in DOM.
  editor.on('PastePostProcess', (event) => {
    // Get inline styles (for spans) - font, color, bold, italic, etc.
    const cursorInlineStyles = getCursorPositionStyles(editor)

    if (cursorInlineStyles) {
      const pastedElement: HTMLElement = event.node

      const textElements = pastedElement.querySelectorAll(
        'p, div, span, h1, h2, h3, h4, h5, h6',
      )

      if (textElements.length > 0) {
        // Convert to array so we can check neighbors
        const paragraphs = Array.from(textElements).filter(
          (el) => (el as HTMLElement).tagName === 'P',
        ) as HTMLElement[]

        paragraphs.forEach((element, index) => {
          const alreadyHasStyledChild = element.querySelector('span[style]')

          if (alreadyHasStyledChild) {
            return
          }

          const innerHTML = element.innerHTML.trim()

          // Handle completely empty paragraphs (blank lines from paste)
          if (!innerHTML) {
            element.outerHTML = `<p style="margin: 0;"><span style="${cursorInlineStyles}">\u200B<br data-mce-bogus="1"></span></p>`
            return
          }

          let finalHTML = ''

          const breaksNeedSplittingIntoSeparateParagraphs =
            regEx.htmlBreakTag.test(innerHTML)

          if (breaksNeedSplittingIntoSeparateParagraphs) {
            // Don't filter out empty lines - we need to preserve blank lines
            const lines = innerHTML.split(regEx.htmlBreakTag)

            // Replace the current paragraph with multiple paragraphs
            // Empty lines become styled blank paragraphs (preserves formatting when clicking them)
            finalHTML = lines
              .map((line) => {
                const trimmedLine = line.trim()

                if (trimmedLine) {
                  // Has content - wrap in styled span
                  const normalizedLine = normalizeHTMLWhitespace(trimmedLine)
                  return `<p style="margin: 0;"><span style="${cursorInlineStyles}">${normalizedLine}</span></p>`
                } else {
                  // Empty line - create blank paragraph with styled span wrapping br
                  // Add zero-width space to prevent TinyMCE from moving br outside span
                  // This preserves formatting when user clicks on the blank line
                  return `<p style="margin: 0;"><span style="${cursorInlineStyles}">\u200B<br data-mce-bogus="1"></span></p>`
                }
              })
              .join('')
          } else {
            // No <br> tags, just wrap content in styled span
            // Normalize whitespace (handles case details with hidden spans)
            const normalizedContent = normalizeHTMLWhitespace(innerHTML)
            finalHTML = `<p style="margin: 0;"><span style="${cursorInlineStyles}">${normalizedContent}</span></p>`
          }

          // Add blank line after each paragraph except the last
          // Paragraph boundaries in paste represent stanza/section separators
          const needsBlankLineAfter = index < paragraphs.length - 1

          const replacement = needsBlankLineAfter
            ? `${finalHTML}<p style="margin: 0;"><span style="${cursorInlineStyles}">\u200B<br data-mce-bogus="1"></span></p>`
            : finalHTML

          element.outerHTML = replacement
        })
      } else {
        // No block elements, apply to the whole node - preserve existing HTML structure
        const innerHTML = pastedElement.innerHTML.trim()

        if (innerHTML) {
          pastedElement.innerHTML = `<span style="${cursorInlineStyles}">${innerHTML}</span>`
        }
      }
    }
    pinEditorBlockFonts(editor, event.node)
  })
}
